import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, MapPin, LogIn, LogOut, RotateCcw, Palmtree } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PageLoader } from "@/components/PageLoader";
import { dispDT, dispDate } from "@/lib/api";

export default function EmployeeHome() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(null); // 'in' | 'out' | null
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [freeActive, setFreeActive] = useState(null);

  const refresh = () =>
    Promise.all([
      api.get("/attendance/today").then((r) => setToday(r.data)),
      api.get("/time/now").then((r) => setTime(r.data)),
      api.get("/free-periods/active").then((r) => setFreeActive(r.data.period)).catch(() => {}),
    ]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    captureLocation();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, []);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Browser tidak mendukung GPS");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success("Lokasi diperbarui");
      },
      () => toast.error("Tidak bisa mengambil lokasi"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const doAbsen = async (kind) => {
    setBusy(kind);
    try {
      const body = coords || {};
      await api.post(`/attendance/${kind}`, body);
      toast.success(`Absen ${kind === "in" ? "Masuk" : "Pulang"} berhasil`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal absen");
    } finally {
      setBusy(null);
    }
  };

  const att = today?.attendance;
  const hasIn = !!att?.in_time;
  const hasOut = !!att?.out_time;
  const isHourly = user?.pay_type === "hourly";
  const phlBlocked = !!freeActive && isHourly;

  if (loading) return <PageLoader label="Memuat data presensi..." />;

  return (
    <div className="px-4 py-6 max-w-md mx-auto space-y-5">
      <div>
        <p className="overline mb-1">Hari Ini</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Halo, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-zinc-500 mt-1" data-testid="today-display">
          {time ? dispDT(time.now) : "Memuat..."}
        </p>
      </div>

      {/* Free Period Banner */}
      {freeActive && (
        <Card
          className={`p-4 border-2 shadow-none ${phlBlocked ? "border-red-300 bg-gradient-to-br from-red-50 to-red-100" : "border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100"}`}
          data-testid="free-period-banner"
        >
          <div className="flex items-start gap-3">
            <Palmtree className={`w-8 h-8 flex-shrink-0 ${phlBlocked ? "text-red-700" : "text-amber-700"}`} />
            <div className="min-w-0 flex-1">
              <p className={`font-display font-bold text-base ${phlBlocked ? "text-red-900" : "text-amber-900"}`}>
                🌴 {freeActive.name}
              </p>
              <p className={`text-xs mt-0.5 ${phlBlocked ? "text-red-800" : "text-amber-800"}`}>
                {dispDate(freeActive.start_date)} — {dispDate(freeActive.end_date)}
              </p>
              <p className={`text-xs mt-2 leading-relaxed ${phlBlocked ? "text-red-800" : "text-amber-800"}`}>
                {phlBlocked
                  ? "Hari ini periode bebas, PHL belum bisa masuk kerja."
                  : "Aturan telat & lembur dinonaktifkan. Absen tetap dianjurkan sebagai penanda kehadiran."}
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Status Presensi</p>
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="status-in">
            <p className="text-xs text-zinc-500">Masuk</p>
            <p className="font-display font-semibold text-lg mt-0.5">
              {hasIn ? att.in_time.split(" ")[1] : "—"}
            </p>
            {att?.in_geo_ok && <Badge variant="outline" className="mt-1 text-[10px] border-green-600 text-green-700">GPS OK</Badge>}
          </div>
          <div data-testid="status-out">
            <p className="text-xs text-zinc-500">Pulang</p>
            <p className="font-display font-semibold text-lg mt-0.5">
              {hasOut ? att.out_time.split(" ")[1] : "—"}
            </p>
            {att?.out_geo_ok && <Badge variant="outline" className="mt-1 text-[10px] border-green-600 text-green-700">GPS OK</Badge>}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-zinc-100 text-xs text-zinc-500 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" />
          {coords
            ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`
            : "Lokasi belum diambil"}
          {today?.geo_enforce && (
            <Badge variant="outline" className="ml-auto text-[10px] border-amber-500 text-amber-700">
              GPS WAJIB
            </Badge>
          )}
        </div>
      </Card>

      <div className="space-y-3">
        <Button
          onClick={() => doAbsen("in")}
          disabled={hasIn || busy !== null || phlBlocked}
          data-testid="btn-absen-masuk"
          className="w-full h-14 bg-primary hover:bg-primary/90 text-base font-semibold"
        >
          {busy === "in" ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <LogIn className="w-5 h-5 mr-2" />
              {phlBlocked ? "Tidak Bisa Absen (Periode Bebas)" : (hasIn ? "Sudah Absen Masuk" : "Absen Masuk")}
            </>
          )}
        </Button>
        <Button
          onClick={() => doAbsen("out")}
          disabled={!hasIn || hasOut || busy !== null || phlBlocked}
          data-testid="btn-absen-pulang"
          variant="outline"
          className="w-full h-14 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white text-base font-semibold"
        >
          {busy === "out" ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <LogOut className="w-5 h-5 mr-2" />
              {hasOut ? "Sudah Absen Pulang" : "Absen Pulang"}
            </>
          )}
        </Button>
        <Button
          onClick={captureLocation}
          variant="ghost"
          data-testid="btn-refresh-loc"
          className="w-full text-sm"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Ambil Ulang Lokasi
        </Button>
      </div>

      <AnnouncementsCard />
    </div>
  );
}

const AnnouncementsCard = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/announcements/me").then((r) => setList(r.data)).catch(() => {});
  }, []);
  if (list.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="announcements">
      {list.map((a) => (
        <Card key={a.id} className="p-4 border-l-4 border-l-primary border-zinc-200 bg-blue-50/40 shadow-none">
          <p className="overline mb-1 text-primary">📢 Pengumuman</p>
          <p className="font-display font-semibold text-base mb-1">{a.title}</p>
          {a.body && <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed">{a.body}</p>}
        </Card>
      ))}
    </div>
  );
};
